import { PRODUCT_PLACEHOLDERS } from "./product-placeholders";
// ---------------------------------------------------------------------------
// HARDWARE PRODUCTS — mirrored from supplier catalog for our own site.
//
// The supplier "hardware" catalog is enormous (1000+ SKUs across snaps,
// grommets, zippers, fasteners, turnbuckles, etc). We mirror by family —
// each card represents a complete product line — and the detail page lists
// the common sizes, finishes and use cases. Customers add to their order
// with notes on size/finish, and we confirm SKUs on the written quote.
//
// Each product has its own internal detail page at /shop/hardware/$slug so
// customers stay on our site instead of being redirected.
// ---------------------------------------------------------------------------

export type HardwareProduct = {
  id: string;
  slug: string;
  name: string;
  /** Headline format shown on the listing card (e.g. "Stainless · Multiple sizes") */
  size: string;
  /** Reference item # (representative SKU from the catalog) */
  itemNumber: string;
  /** Brand / manufacturer */
  manufacturer: string;
  /** Short blurb shown on the listing card */
  blurb: string;
  /** Long description shown on the detail page (paragraphs) */
  description: string[];
  /** Bullet-point feature list */
  features: string[];
  /** Spec table key/value pairs */
  specs: { label: string; value: string }[];
  /** Product image URL */
  image: string;
  /** Available finishes (for the picker on the detail page) */
  finishes: string[];
  /** Available sizes / configurations */
  sizes: string[];
  /** Optional note ("Sold per piece", "Box of 100", etc.) */
  productNote?: string;
};

export const hardwareProducts: HardwareProduct[] = [
  {
    id: "dot-durable-snap-fasteners",
    slug: "dot-durable-snap-fasteners",
    name: "DOT® Durable™ Snap Fasteners",
    size: "Stainless / Brass · Multiple finishes",
    itemNumber: "100100",
    manufacturer: "DOT Fastener",
    blurb:
      "The industry-standard snap for awnings, boat covers and convertible tops. Long-lasting brass or stainless construction.",
    description: [
      "DOT® Durable™ snap fasteners are the most widely-used canvas snaps in the awning and marine trades. Each set is made of solid brass (with optional nickel or stainless plating) and consists of four parts — cap, socket, stud and eyelet — installed with a snap-setting tool.",
      "Used to attach boat covers, bimini tops, awning valances, drop curtains, dodgers and a thousand other canvas-to-frame applications. Their reliability is the reason they've been the industry default for decades.",
      "Available individually or in 100-count tradesman boxes. Color the cap to match Sunbrella® canvas, or pick a marine-grade stainless cap for offshore use.",
    ],
    features: [
      "Industry-standard 4-part snap (cap / socket / stud / eyelet)",
      "Solid brass construction with multiple plating options",
      "Stainless option for offshore marine use",
      "Cap colors to match common canvas",
      "Sold individually or in 100-count boxes",
    ],
    specs: [
      { label: "Product Category", value: "Snap Fasteners" },
      { label: "Material", value: "Brass / Stainless Steel" },
      { label: "Parts per Set", value: "4 (cap, socket, stud, eyelet)" },
      { label: "Manufacturer", value: "DOT Fastener" },
      { label: "Installation", value: "DOT setting tool or hand press" },
    ],
    image: PRODUCT_PLACEHOLDERS.hardware,
    finishes: ["Nickel-plated brass", "Solid brass", "Stainless steel", "Black oxide"],
    sizes: ['Standard cap (3/8")', 'Long-shank stud (5/8")', "100-count tradesman box"],
    productNote: "Sold individually or in 100-count boxes — tell us what you need in your order notes.",
  },
  {
    id: "stimpson-self-piercing-grommets",
    slug: "stimpson-self-piercing-grommets",
    name: "Stimpson® Self-Piercing Grommets & Washers",
    size: "Sizes #0–#4 · Brass / Stainless / Nickel",
    itemNumber: "200200",
    manufacturer: "Stimpson",
    blurb:
      "Heavy-duty self-piercing grommets for banners, tarps, awnings, sail shades and tie-down corners.",
    description: [
      "Stimpson® self-piercing grommets are the workhorse of every canvas shop. The two-part grommet (top + washer) is set with a hand or bench press and creates a clean, durable hole that won't tear out under load — the right hardware for tarp corners, sail-shade attachment points, banner reinforcements and awning lacing.",
      "Available in sizes #0 (3/16\" hole) through #4 (1/2\" hole) and in brass, nickel-plated brass and stainless steel finishes. We stock the common sizes (#1, #2 and #4) and can order any size needed.",
      "Sold per set (top + washer), in bags of 144 (a gross), or in tradesman boxes of 1,000.",
    ],
    features: [
      "Self-piercing — no separate hole punch required",
      'Sizes #0 (3/16") through #4 (1/2")',
      "Brass, nickel and stainless finishes",
      "Set with hand press, bench press or pneumatic press",
      "Sold per piece, gross, or 1,000-count box",
    ],
    specs: [
      { label: "Product Category", value: "Grommets & Washers" },
      { label: "Sizes", value: '#0 (3/16") · #1 (1/4") · #2 (3/8") · #4 (1/2")' },
      { label: "Material", value: "Brass / Nickel-Plated Brass / Stainless" },
      { label: "Manufacturer", value: "Stimpson" },
      { label: "Set With", value: "Stimpson hand or bench press" },
    ],
    image: PRODUCT_PLACEHOLDERS.hardware,
    finishes: ["Brass", "Nickel-plated brass", "Stainless steel"],
    sizes: ["#0", "#1", "#2", "#3", "#4", "Gross (144 ct)", "1,000-count box"],
    productNote: "Tell us size, finish, and quantity — we'll confirm pricing on the quote.",
  },
  {
    id: "ykk-vislon-marine-zippers",
    slug: "ykk-vislon-marine-zippers",
    name: "YKK® VISLON® #10 Marine Zipper Chain & Sliders",
    size: '#10 chain · By the yard + sliders',
    itemNumber: "300300",
    manufacturer: "YKK",
    blurb:
      "Heavy-duty molded plastic zipper for boat covers, dodgers, enclosures and convertible tops. UV-resistant, salt-water-safe.",
    description: [
      "YKK® VISLON® #10 is the standard heavy-duty zipper for marine canvas, dodgers, boat enclosures, convertible tops and outdoor structures. The chunky molded polyacetal teeth are immune to salt water and UV, won't corrode like a metal zipper, and slide smoothly under load.",
      "We sell the chain by the yard and the sliders separately, so you only buy the length you need. Common slider styles: single non-locking, double non-locking, single auto-lock, and double auto-lock — pick what your application needs.",
      "Available in black and white. Pair with a YKK® stop-and-box set to terminate the zipper cleanly.",
    ],
    features: [
      "#10 size — heavy duty for outdoor / marine use",
      "Molded polyacetal teeth — salt-water + UV-safe",
      "Chain sold by the yard for any length",
      "Sliders sold separately (single / double, locking / non-locking)",
      "Black and white standard colors",
    ],
    specs: [
      { label: "Product Category", value: "Marine Zipper" },
      { label: "Chain Size", value: "#10 (~10 mm)" },
      { label: "Tooth Material", value: "Molded Polyacetal" },
      { label: "Manufacturer", value: "YKK" },
      { label: "Sold As", value: "Chain by the yard + sliders separately" },
    ],
    image: PRODUCT_PLACEHOLDERS.hardware,
    finishes: ["Black", "White"],
    sizes: ["Chain — by the yard", "Single non-lock slider", "Double non-lock slider", "Single auto-lock slider", "Double auto-lock slider", "Stop & box set"],
    productNote: "Tell us total yards of chain and how many sliders — we'll quote a complete kit.",
  },
  {
    id: "stainless-turnbuckles-shade-sail",
    slug: "stainless-turnbuckles-shade-sail",
    name: "Stainless Steel Turnbuckles — Shade Sail Hardware",
    size: '1/4" – 1/2" · 316 marine stainless',
    itemNumber: "400400",
    manufacturer: "Marine Hardware",
    blurb:
      "Marine-grade 316 stainless turnbuckles for shade sails, awnings, dock canopies and tensioned outdoor structures.",
    description: [
      "Tensioning hardware for shade sails, large awnings and any tensioned canvas application. Made of 316 marine-grade stainless steel — the right grade for coastal Texas, pool decks and any salt-air exposure where lower grades would rust and stain the fabric.",
      "Available in jaw-and-jaw, jaw-and-eye and eye-and-eye configurations to match your hardware kit. Common sizes: 1/4\", 5/16\", 3/8\" and 1/2\" — pick based on the sail size and wind load.",
      "Pair with stainless snap shackles, S-hooks, D-rings, and pad-eyes to build a complete shade-sail tension kit.",
    ],
    features: [
      "316 marine-grade stainless steel",
      "Jaw-and-jaw / jaw-and-eye / eye-and-eye configurations",
      'Sizes 1/4", 5/16", 3/8", 1/2"',
      "Coastal-rated — won't rust or stain fabric",
      "Compatible with full shade-sail hardware kit",
    ],
    specs: [
      { label: "Product Category", value: "Tension Hardware" },
      { label: "Material", value: "316 Stainless Steel" },
      { label: "Sizes", value: '1/4" · 5/16" · 3/8" · 1/2"' },
      { label: "Configurations", value: "Jaw-Jaw / Jaw-Eye / Eye-Eye" },
      { label: "Application", value: "Shade sails, tensioned awnings, marine" },
    ],
    image: PRODUCT_PLACEHOLDERS.hardware,
    finishes: ["316 Stainless Steel"],
    sizes: ['1/4"', '5/16"', '3/8"', '1/2"'],
    productNote: 'Tell us sail size and corner count — we\'ll quote a full kit.',
  },
  {
    id: "velcro-brand-hook-loop-tape",
    slug: "velcro-brand-hook-loop-tape",
    name: "VELCRO® Brand Hook & Loop Fastener Tape",
    size: '1" / 1.5" / 2" wide · Adhesive or sew-on',
    itemNumber: "500500",
    manufacturer: "VELCRO® Brand",
    blurb:
      "Genuine VELCRO® Brand hook and loop tape — sew-on or pressure-sensitive adhesive back, sold by the yard.",
    description: [
      'Genuine VELCRO® Brand hook and loop fastener tape for canvas drop curtains, removable covers, screen panels, signage, and dozens of canvas applications where a re-openable closure is needed. We sell the real Velcro brand — not generic — so the closure is consistent and lasts.',
      "Available in three widths (1\", 1.5\", 2\"), in either sew-on (standard back nylon or polyester tape) or pressure-sensitive adhesive back. Colors: black and white in stock; beige and other colors by special order.",
      'Sold by the linear yard. Hook and loop sides are sold separately — order matching yardage of each.',
    ],
    features: [
      "Genuine VELCRO® Brand — not generic hook & loop",
      'Three widths: 1", 1.5", 2"',
      "Sew-on (nylon or polyester) or pressure-sensitive adhesive back",
      "Black and white standard, other colors by special order",
      "Sold by the yard — hook and loop ordered separately",
    ],
    specs: [
      { label: "Product Category", value: "Hook & Loop Fastener" },
      { label: "Brand", value: "VELCRO® Brand (genuine)" },
      { label: "Widths", value: '1" · 1.5" · 2"' },
      { label: "Backings", value: "Sew-on / Pressure-sensitive adhesive" },
      { label: "Standard Colors", value: "Black, White" },
    ],
    image: PRODUCT_PLACEHOLDERS.hardware,
    finishes: ["Black", "White", "Beige (special order)"],
    sizes: ['1" wide — by the yard', '1.5" wide — by the yard', '2" wide — by the yard', "Sew-on", "Adhesive back"],
    productNote: "Order matching yards of hook + loop — we'll confirm width and backing on the quote.",
  },
  {
    id: "tenara-marine-fasteners-snap-hooks",
    slug: "tenara-marine-fasteners-snap-hooks",
    name: "Marine Snap Hooks, D-Rings & Pad Eyes",
    size: "316 stainless · Multiple sizes",
    itemNumber: "600600",
    manufacturer: "Marine Hardware",
    blurb:
      "Stainless snap hooks, D-rings, pad eyes and S-hooks — the small hardware that completes every shade sail, boat cover and tie-down kit.",
    description: [
      "The supporting cast for every shade sail, boat cover, awning, dodger and tie-down job. Stainless snap hooks attach corners to anchors, D-rings reinforce strap loops, pad eyes mount to walls and decks, and S-hooks chain components together.",
      "All 316 marine-grade stainless steel. Sizes from small upholstery hooks up to heavy 3/8\" pad eyes for shade-sail anchor points. Tell us what you're attaching and we'll spec the right combination.",
      "Sold individually or in shade-sail hardware kits (4 turnbuckles + 4 snap hooks + 4 pad eyes — sized to your sail).",
    ],
    features: [
      "316 marine-grade stainless steel",
      "Snap hooks, D-rings, pad eyes, S-hooks",
      "Multiple sizes — small upholstery to heavy structural",
      "Sold individually or as complete kits",
      "Pair with our turnbuckles for a full tension system",
    ],
    specs: [
      { label: "Product Category", value: "Marine & Shade Hardware" },
      { label: "Material", value: "316 Stainless Steel" },
      { label: "Components", value: "Snap hooks · D-rings · Pad eyes · S-hooks" },
      { label: "Application", value: "Shade sails, boat covers, awnings, tie-downs" },
      { label: "Sold As", value: "Individually or in matched kits" },
    ],
    image: PRODUCT_PLACEHOLDERS.hardware,
    finishes: ["316 Stainless Steel"],
    sizes: ["Small (upholstery)", "Medium (boat cover)", "Large (shade sail)", "Heavy (structural)", "4-corner shade-sail kit"],
    productNote: "Describe your project — we'll spec the right hardware mix and quote a kit.",
  },
];

export function getHardwareBySlug(slug: string): HardwareProduct | undefined {
  return hardwareProducts.find((p) => p.slug === slug);
}
